import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Button,
  MenuItem,
  FormControl,
  FormControlLabel,
  Checkbox,
  Select,
  InputLabel,
  Typography,
  TextField,
  Box,
  IconButton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useRecordAudio } from "../../components/configurator/utils/utils";
import { AudioPlayerControlSprite, Audio } from "react-audio-player-pro";
import "react-audio-player-pro/dist/style.css";
import {
  allThemes,
  themeFilters,
} from "../../components/configurator/constants";
import { Close } from "@mui/icons-material";
import { myThemesServices } from "../../services";

const PREFIX = "ThemesMapForm";

const classes = {
  themeFieldsContainer: `${PREFIX}-themeFieldsContainer`,
  marginBottom: `${PREFIX}-marginBottom`,
  marginTop: `${PREFIX}-marginTop`,
  musicContainer: `${PREFIX}-musicContainer`,
  recordTimer: `${PREFIX}-recordTimer`,
  musicTitleEllipsis: `${PREFIX}-musicTitleEllipsis`,
  buttonsContainer: `${PREFIX}-buttonsContainer`,
  importSongButton: `${PREFIX}-importSongButton`,
  checkboxLoop: `${PREFIX}-checkboxLoop`,
  createButton: `${PREFIX}-createButton`,
  selectContainer: `${PREFIX}-selectContainer`,
  marginRight: `${PREFIX}-marginRight`,
};

const ThemesMapFormContainer = styled("div")(({ theme }) => {
  return {
    [`& .${classes.themeFieldsContainer}`]: {
      margin: "0.5rem 0 1rem 0",
      width: "300px",
      [theme.breakpoints.between("xs", "sm")]: {
        width: "100%",
      },
    },
    [`& .${classes.marginBottom}`]: {
      marginBottom: "1rem",
    },
    [`& .${classes.marginTop}`]: {
      marginTop: "1rem",
    },
    [`& .${classes.musicContainer}`]: {
      marginBottom: "1rem !important",
    },
    [`& .${classes.recordTimer}`]: {
      display: "flex",
      justifyContent: "center",
      marginBottom: "1rem",
    },
    [`& .${classes.musicTitleEllipsis}`]: {
      maxWidth: "275px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    [`& .${classes.buttonsContainer}`]: {
      display: "flex",
      justifyContent: "center",
    },
    [`& .${classes.importSongButton}`]: {
      display: "flex",
      justifyContent: "center",
      height: "31px",
    },
    [`& .${classes.checkboxLoop}`]: {
      display: "flex",
      justifyContent: "center",
    },
    [`& .${classes.createButton}`]: {
      marginTop: "0.5rem",
      width: "100%",
    },
    [`& .${classes.selectContainer}`]: {
      margin: "1rem 0",
    },
    [`& .${classes.marginRight}`]: {
      marginRight: "0.5rem",
    },
  };
});

const ThemesMapForm = ({ handleCreateTheme, currentTheme }) => {
  const { t } = useTranslation();
  const [formState, setFormState] = useState({
    theme: "",
    filter: "",
    sound: null,
    soundPreview: null,
    soundName: "",
    image: null,
    imagePreview: null,
    imageName: "",
    loop: false,
    title: "",
  });

  useEffect(() => {
    if (currentTheme) {
      setFormState({
        theme:
          currentTheme.ambiantMusic && currentTheme.theme !== "song"
            ? "songAndFilter"
            : currentTheme.ambiantMusic && !currentTheme.theme
            ? "song"
            : currentTheme.theme,
        filter: currentTheme.theme,
        title: currentTheme.title,
        sound: currentTheme.ambiantMusic || null,
        image: currentTheme.previewUrl || null,
        loop: currentTheme.loop || false,
      });
    }
  }, [currentTheme]);

  const handleChange = (field, value) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // This function handles file uploads specifically
  const handleFileChange = (e, isSound = false) => {
    const myKeys = isSound
      ? ["sound", "soundPreview", "soundName"]
      : ["image", "imagePreview", "imageName"];
    if (e.target.files && e.target.files.length > 0) {
      const myUrl = URL.createObjectURL(e.target.files[0]);
      setFormState((prev) => ({
        ...prev,
        [myKeys[0]]: e.target.files,
        [myKeys[1]]: myUrl,
        [myKeys[2]]: e.target.files[0].name,
      }));
    }
  };

  const handleThemeChange = (event) => {
    const value = event.target.value;
    handleChange("theme", value);

    // Reset filter when changing theme type
    if (value !== "songAndFilter" && formState.filter.length > 0) {
      handleChange("filter", "");
    }
  };

  const handleAudioName = (url) => {
    if (!url) return "";
    const parts = url.split("/");
    return parts[parts.length - 1];
  };

  // Initialize the record audio hook with the proper parameters
  const myRecordAudio = useRecordAudio(formState, setFormState, true);

  const isFormValid = () => {
    const { theme, sound, filter, title } = formState;
    if (!theme || !title) return false;
    if (theme === "song" && !sound) return false;
    if (theme === "songAndFilter" && (!sound || !filter)) return false;

    return true;
  };

  const handleSubmit = () => {
    if (isFormValid()) {
      const dataToSend = {
        ...currentTheme,
        title: formState.title,
        theme: formState.filter || formState.theme,
        loop: formState.loop,
      };
      if (formState.soundPreview) dataToSend.ambiantMusic = null;
      if (formState.imagePreview) dataToSend.previewUrl = null;

      myThemesServices
        .createOrUpdateTheme(
          dataToSend,
          formState.soundPreview,
          formState.soundName,
          formState.imagePreview,
          formState.imageName,
          currentTheme ? false : true
        )
        .then((res) => {
          // then call handleCreateTheme to refresh the list
          handleCreateTheme();
        })
        .catch((err) => {
          console.error(err);
        });
    }
  };

  const isSoundType =
    formState.theme === "song" || formState.theme === "songAndFilter";
  const showFilterSelect = formState.theme === "songAndFilter";

  const handleDeleteThemeImage = () => {
    setFormState({
      ...formState,
      image: null,
      imageName: "",
      imagePreview: null,
    });
  };

  return (
    <ThemesMapFormContainer sx={{ display: "flex", justifyContent: "center" }}>
      <div className={classes.themeFieldsContainer}>
        <Typography variant="h6" className={classes.marginBottom}>
          {t("geolocThemesForm.mainTitle")}
        </Typography>

        {/* Title field  */}
        <TextField
          label={t("geolocThemesForm.title")}
          type="text"
          value={formState.title}
          onChange={(e) => {
            handleChange("title", e.target.value);
          }}
          fullWidth
          className={classes.marginBottom}
        />

        <Typography variant="body1" className={classes.marginBottom}>
          {t("geolocThemesForm.themeImageTitle")}
        </Typography>
        {(formState.imagePreview || formState.image) && (
          <Box sx={{ position: "relative" }}>
            <IconButton
              onClick={handleDeleteThemeImage}
              color="primary"
              sx={{ position: "absolute", top: 0, right: 0 }}
            >
              <Close />
            </IconButton>
            <img
              src={formState.imagePreview || formState.image}
              alt="preview theme"
              style={{
                width: "100%",
                maxHeight: "100px",
                objectFit: "contain",
                marginBottom: "1rem",
              }}
            />
          </Box>
        )}

        {/* image field  */}
        <Button
          variant="contained"
          color="primary"
          component="label"
          className={classes.importSongButton}
          sx={{ maxWidth: "75%", margin: "0 auto" }}
        >
          {t("Import an image")}
          <input
            type="file"
            accept="image/*"
            name="themeImage"
            onChange={(e) => handleFileChange(e, false)}
            hidden
          />
        </Button>

        {/* Theme Type Select */}
        <FormControl fullWidth className={classes.selectContainer}>
          <InputLabel id="theme-type-label">
            {t("geolocThemesForm.themeType")}
          </InputLabel>
          <Select
            labelId="theme-type-label"
            id="theme-type"
            value={formState.theme}
            label={t("geolocThemesForm.themeType")}
            onChange={handleThemeChange}
          >
            {allThemes.map((theme) => (
              <MenuItem key={theme} value={theme}>
                {theme}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Filter select - show for appropriate themes */}
        {showFilterSelect && (
          <FormControl fullWidth className={classes.marginBottom}>
            <InputLabel id="filter-label">
              {t("geolocThemesForm.themeFilterType")}
            </InputLabel>
            <Select
              labelId="filter-label"
              id="filter"
              value={formState.filter}
              label={t("geolocThemesForm.themeFilterType")}
              onChange={(e) => handleChange("filter", e.target.value)}
            >
              {themeFilters(t).map((filter) => (
                <MenuItem key={filter.value} value={filter.value}>
                  {filter.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Audio section for song/songAndFilter */}
        {isSoundType && (
          <div className={classes.musicContainer}>
            <Typography variant="subtitle1" className={classes.marginBottom}>
              {t("Audio")}
            </Typography>

            {/* Show recording timer when recording */}
            {myRecordAudio.isRecording && (
              <Typography variant="h5" className={classes.recordTimer}>
                {myRecordAudio.timer}
              </Typography>
            )}

            {/* Show audio name and player if there's a preview */}
            {(formState.soundPreview || formState.sound) &&
              !myRecordAudio.isRecording && (
                <>
                  <Typography
                    variant="body1"
                    className={classes.musicTitleEllipsis}
                  >
                    {formState.soundType ||
                      handleAudioName(
                        formState.soundPreview || formState.sound
                      )}
                  </Typography>
                  <div className={classes.marginBottom}>
                    <AudioPlayerControlSprite />
                    <Audio
                      src={formState.soundPreview || formState.sound}
                      preload="auto"
                      downloadFileName={formState.soundType || "audio"}
                    />
                  </div>
                </>
              )}

            {/* Record and import buttons */}
            <div className={classes.buttonsContainer}>
              {/* Use the recordButton from the hook */}
              {myRecordAudio.recordButton}

              {/* Import button */}
              {!myRecordAudio.isRecording && (
                <Button
                  variant="contained"
                  color="primary"
                  component="label"
                  size="small"
                  className={classes.importSongButton}
                >
                  {t("geolocThemesForm.ImportButton")}
                  <input
                    type="file"
                    accept=".mp3,.wav,.ogg"
                    name="radiusMusic"
                    onChange={(e) => handleFileChange(e, true)}
                    hidden
                  />
                </Button>
              )}
            </div>

            {/* Loop checkbox - only show when we have audio */}
            {(formState.soundPreview || formState.sound) &&
              !myRecordAudio.isRecording && (
                <FormControlLabel
                  control={
                    <Checkbox
                      color={"primary"}
                      onChange={(e) => handleChange("loop", e.target.checked)}
                      checked={formState.loop}
                    />
                  }
                  label={t("geolocThemesForm.loop")}
                  className={classes.checkboxLoop}
                />
              )}
          </div>
        )}

        {/* Create button */}
        <Button
          variant="contained"
          color="primary"
          disabled={!isFormValid()}
          onClick={handleSubmit}
          className={classes.createButton}
        >
          {currentTheme
            ? t("geolocThemesForm.updateButton")
            : t("geolocThemesForm.createButton")}
        </Button>
      </div>
    </ThemesMapFormContainer>
  );
};

export default ThemesMapForm;
